// src/components/security/LockedCard.tsx
/**
 * What a card shows instead of its contents when this session has not earned them.
 *
 * The sessions list and the peer reset both require step-up on the server (`@AdminStepUp` on
 * `AdminSecurityController`), so rendering their controls to an unverified session would only
 * produce a 403 on the first click. Showing the lock instead states the requirement before the
 * click, and — this is the part that matters — offers the way past it on the same card.
 *
 * The two reasons a card is locked need different words because they need different actions: an
 * account with no enrolment cannot verify anything and has to use the TOTP card above; an enrolled
 * account just has not proved it yet this session and can do so right here. Only the second case
 * gets a button.
 *
 * The button opens the ONE global step-up modal (`StepUpModalHost`, mounted in `App.tsx`) by
 * dispatching into the store, and deliberately non-blocking: the page behind it is the security page
 * itself, which is the way IN — a modal that could not be dismissed here would be a lock with the
 * key on the inside.
 */
import React from 'react';
import { useDispatch } from 'react-redux';
import { openStepUp } from '../../store/mfaSlice';
import { Card, CardTitle, Hint, Primary } from './ui';

interface LockedCardProps {
    title: string;
    /** Whether the ACCOUNT has TOTP enrolled — not whether this session is verified. A locked card is
     *  only ever rendered when the session is unverified, so this picks which of the two reasons. */
    enabled: boolean;
}

const LockedCard: React.FC<LockedCardProps> = ({ title, enabled }) => {
    const dispatch = useDispatch();

    return (
        <Card>
            <CardTitle>{title}</CardTitle>
            {enabled ? (
                <>
                    <Hint style={{ margin: '0 0 14px' }}>
                        Táto časť sa odomkne po overení druhým faktorom v tejto relácii.
                    </Hint>
                    <Primary type="button" onClick={() => dispatch(openStepUp({ blocking: false }))}>
                        Overiť teraz
                    </Primary>
                </>
            ) : (
                <Hint style={{ margin: 0 }}>Táto časť sa odomkne po zapnutí dvojstupňového overenia.</Hint>
            )}
        </Card>
    );
};

export default LockedCard;
